import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Search,
  Pets,
  Close,
  Phone,
  Email,
  CalendarToday,
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
  Person,
  CreditCard,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

interface PetOwner {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  profileComplete: boolean;
  platform?: string;
  language?: 'en' | 'de';
  petCount: number;
  hasPaymentMethod: boolean;
  lastLogin?: string;
  createdAt: string;
  isActive: boolean;
}

interface PetDetail {
  id: string;
  name: string;
  species: string;
  breed: string;
  age: number;
  weight: number;
  gender: string;
  neutered: boolean;
  vaccinated: boolean;
  medications: string[];
  specialNeeds: string;
  personality: string;
  photo?: string;
  createdAt: string;
}

interface OwnerDetail {
  owner: PetOwner & { address?: any; hasPaymentMethod?: boolean };
  pets: PetDetail[];
}

const speciesEmoji: Record<string, string> = {
  dog: '🐕',
  cat: '🐱',
  bird: '🐦',
  rabbit: '🐰',
  other: '🐾',
};

interface PetOwnerProfilesListProps {
  onClose: () => void;
}

const PetOwnerProfilesList: React.FC<PetOwnerProfilesListProps> = ({ onClose }) => {
  const [owners, setOwners] = useState<PetOwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedOwner, setSelectedOwner] = useState<OwnerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [expandedPetId, setExpandedPetId] = useState<string | null>(null);

  useEffect(() => {
    fetchOwners();
  }, []);

  const fetchOwners = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/api/admin/pet-owners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOwners(data.owners || []);
      } else {
        console.error('Pet owners fetch failed:', res.status, await res.text());
      }
    } catch (error) {
      console.error('Failed to fetch pet owners:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnerDetail = async (ownerId: string) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/api/admin/pet-owners/${ownerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedOwner(data);
      }
    } catch (error) {
      console.error('Failed to fetch owner detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredOwners = owners.filter((o) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      o.firstName?.toLowerCase().includes(q) ||
      o.lastName?.toLowerCase().includes(q) ||
      o.email?.toLowerCase().includes(q) ||
      o.phoneNumber?.includes(q)
    );
  });

  const formatDate = (d?: string) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (d?: string) => {
    if (!d) return 'Never';
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Pet Owner Profiles
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {owners.length} registered pet owner{owners.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => { setLoading(true); fetchOwners(); }} variant="outlined" size="small">
            Refresh
          </Button>
          <Button onClick={onClose} variant="outlined" size="small">
            Close
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Total Owners</Typography>
              <Typography variant="h5" fontWeight="bold">{owners.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">With Pets</Typography>
              <Typography variant="h5" fontWeight="bold">{owners.filter(o => o.petCount > 0).length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Total Pets</Typography>
              <Typography variant="h5" fontWeight="bold">{owners.reduce((sum, o) => sum + o.petCount, 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Profile Complete</Typography>
              <Typography variant="h5" fontWeight="bold">{owners.filter(o => o.profileComplete).length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by name, email, or phone..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Owner</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="center">Pets</TableCell>
                <TableCell align="center">Profile</TableCell>
                <TableCell align="center">Payment</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Language</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOwners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {search ? 'No pet owners match your search' : 'No pet owners registered yet'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOwners.map((owner) => (
                  <TableRow
                    key={owner.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => fetchOwnerDetail(owner.id)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#e3f2fd', color: '#1565c0', fontSize: 14 }}>
                          {owner.firstName?.[0]}{owner.lastName?.[0]}
                        </Avatar>
                        <Typography variant="body2" fontWeight="600">
                          {owner.firstName} {owner.lastName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{owner.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{owner.phoneNumber || '—'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<Pets sx={{ fontSize: 14 }} />}
                        label={owner.petCount}
                        size="small"
                        variant={owner.petCount > 0 ? 'filled' : 'outlined'}
                        color={owner.petCount > 0 ? 'primary' : 'default'}
                        sx={{ minWidth: 50 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {owner.profileComplete ? (
                        <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                      ) : (
                        <Cancel sx={{ color: 'text.disabled', fontSize: 20 }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {owner.hasPaymentMethod ? (
                        <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                      ) : (
                        <Cancel sx={{ color: 'text.disabled', fontSize: 20 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={owner.platform || 'Unknown'}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={owner.language === 'de' ? 'Deutsch' : 'English'} color={owner.language === 'de' ? 'info' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{formatDate(owner.createdAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{formatDateTime(owner.lastLogin)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={owner.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={owner.isActive ? 'success' : 'default'}
                        variant={owner.isActive ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Owner Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelectedOwner(null); setExpandedPetId(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">Pet Owner Profile</Typography>
          <IconButton onClick={() => { setDetailOpen(false); setSelectedOwner(null); setExpandedPetId(null); }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : selectedOwner ? (
            <Box>
              {/* Owner Info */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ width: 56, height: 56, bgcolor: '#e3f2fd', color: '#1565c0', fontSize: 22 }}>
                      {selectedOwner.owner.firstName?.[0]}{selectedOwner.owner.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {selectedOwner.owner.firstName} {selectedOwner.owner.lastName}
                      </Typography>
                      <Chip
                        label="Pet Owner"
                        size="small"
                        sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 500 }}
                      />
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2">{selectedOwner.owner.email}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2">{selectedOwner.owner.phoneNumber || 'Not provided'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2">Joined {formatDate(selectedOwner.owner.createdAt)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2">Last login: {formatDateTime(selectedOwner.owner.lastLogin)}</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Profile</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            icon={selectedOwner.owner.profileComplete ? <CheckCircle /> : <Cancel />}
                            label={selectedOwner.owner.profileComplete ? 'Complete' : 'Incomplete'}
                            size="small"
                            color={selectedOwner.owner.profileComplete ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Pets</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            icon={<Pets />}
                            label={`${selectedOwner.pets.length} pet${selectedOwner.pets.length !== 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            icon={<CreditCard />}
                            label={selectedOwner?.owner.hasPaymentMethod ? 'Configured' : 'Not configured'}
                            size="small"
                            variant={selectedOwner?.owner.hasPaymentMethod ? 'filled' : 'outlined'}
                            color={selectedOwner?.owner.hasPaymentMethod ? 'success' : 'default'}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Pets Section */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
                Pets ({selectedOwner.pets.length})
              </Typography>

              {selectedOwner.pets.length === 0 ? (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Pets sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No pets added yet</Typography>
                  </CardContent>
                </Card>
              ) : (
                selectedOwner.pets.map((pet) => (
                  <Card key={pet.id} variant="outlined" sx={{ mb: 1.5 }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => setExpandedPetId(expandedPetId === pet.id ? null : pet.id)}
                      >
                        <Avatar
                          src={pet.photo || undefined}
                          sx={{ width: 40, height: 40, bgcolor: '#f5f5f5', mr: 1.5, fontSize: 20 }}
                        >
                          {!pet.photo && (speciesEmoji[pet.species] || '🐾')}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="600">{pet.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {pet.breed} · {pet.age} {pet.age === 1 ? 'yr' : 'yrs'} · {pet.weight} kg · {pet.gender === 'male' ? 'Male' : 'Female'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {pet.vaccinated && (
                            <Chip label="Vaccinated" size="small" color="success" variant="outlined" sx={{ fontSize: 11 }} />
                          )}
                          {pet.neutered && (
                            <Chip label="Neutered" size="small" color="info" variant="outlined" sx={{ fontSize: 11 }} />
                          )}
                          {pet.specialNeeds && (
                            <Chip label="Special needs" size="small" color="warning" variant="outlined" sx={{ fontSize: 11 }} />
                          )}
                          <IconButton size="small">
                            {expandedPetId === pet.id ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Box>
                      </Box>

                      <Collapse in={expandedPetId === pet.id}>
                        <Divider sx={{ my: 1.5 }} />
                        {pet.photo && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Photo</Typography>
                            <Box
                              component="img"
                              src={pet.photo}
                              alt={pet.name}
                              sx={{
                                width: 120,
                                height: 120,
                                borderRadius: 2,
                                objectFit: 'cover',
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            />
                          </Box>
                        )}
                        <Grid container spacing={1.5}>
                          {pet.personality && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Personality</Typography>
                              <Typography variant="body2">{pet.personality}</Typography>
                            </Grid>
                          )}
                          {pet.specialNeeds && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Special Needs</Typography>
                              <Typography variant="body2">{pet.specialNeeds}</Typography>
                            </Grid>
                          )}
                          {pet.medications && pet.medications.length > 0 && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Medications</Typography>
                              <Typography variant="body2">{pet.medications.join(', ')}</Typography>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Added</Typography>
                            <Typography variant="body2">{formatDate(pet.createdAt)}</Typography>
                          </Grid>
                        </Grid>
                      </Collapse>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDetailOpen(false); setSelectedOwner(null); setExpandedPetId(null); }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PetOwnerProfilesList;
